import { useState, useMemo, forwardRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, CalendarCheck, MessageCircle } from "lucide-react";
import { PACKAGES, EXTRAS, SIZES, CONTACT } from "../data/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Booking = forwardRef(({ selection, setSelection }, ref) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    plate: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [location, setLocation] = useState("");
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [checking, setChecking] = useState(false);

  const { pkg, size, extras } = selection;

  const travelSurcharge =
    distanceInfo && distanceInfo.found ? distanceInfo.surcharge : 0;

  const total = useMemo(() => {
    let t = 0;
    const selectedPkg = PACKAGES.find((p) => p.id === pkg);
    if (selectedPkg) t += selectedPkg.prices[size];
    extras.forEach((eid) => {
      const ex = EXTRAS.find((e) => e.id === eid);
      if (ex) t += ex.prices[size];
    });
    t += travelSurcharge;
    return t;
  }, [pkg, size, extras, travelSurcharge]);

  const checkZone = async () => {
    if (!location.trim()) return toast.error("Inserisci la tua città o indirizzo");
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/check-distance`, { location });
      setDistanceInfo(data);
      if (!data.found) {
        toast.error("Località non trovata. Prova con il nome del comune.");
      } else if (data.within_radius) {
        toast.success(`Sei a ${data.distance_km} km da Bressanone: trasferta gratuita!`);
      } else {
        toast(`Sei a ${data.distance_km} km da Bressanone: +${data.surcharge}€ di trasferta.`);
      }
    } catch (err) {
      toast.error("Errore nella verifica. Riprova.");
    } finally {
      setChecking(false);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleExtra = (id) =>
    setSelection((s) => ({
      ...s,
      extras: s.extras.includes(id)
        ? s.extras.filter((e) => e !== id)
        : [...s.extras, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!pkg) return toast.error("Seleziona un pacchetto (Base o Premium)");
    if (!location.trim()) return toast.error("Inserisci il luogo del servizio (città/indirizzo)");
    if (!distanceInfo || !distanceInfo.found)
      return toast.error("Verifica la tua zona prima di prenotare");

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        package: pkg,
        size,
        extras,
        service_mode: "domicilio",
        location,
        ...form,
      });
      setConfirmation({
        ...data,
        name: form.first_name,
        whatsappUrl: `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
          [
            `Ciao Cosimiroclean! Ho appena inviato una richiesta di prenotazione.`,
            `Nome: ${form.first_name} ${form.last_name}`,
            `Telefono: ${form.phone}`,
            `Pacchetto: ${PACKAGES.find((p) => p.id === pkg).name}`,
            `Dimensione auto: ${SIZES.find((s) => s.id === size).label}`,
            extras.length
              ? `Extra: ${extras.map((eid) => EXTRAS.find((e) => e.id === eid).name).join(", ")}`
              : null,
            form.plate ? `Targa: ${form.plate}` : null,
            `Luogo: ${location}`,
            `Totale stimato: ${data.total}€`,
            form.notes ? `Note: ${form.notes}` : null,
            `Quando possiamo fissare l'appuntamento?`,
          ]
            .filter(Boolean)
            .join("\n")
        )}`,
      });
      toast.success(`Richiesta inviata! Totale stimato ${data.total}€`);
      setForm({
        first_name: "", last_name: "",
        phone: "", email: "", plate: "", notes: "",
      });
      setSelection({ pkg: null, size: "medium", extras: [] });
      setLocation("");
      setDistanceInfo(null);
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o contattaci direttamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-[#050B18]/70 border border-slate-500/25 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#7EC8E3] focus:ring-1 focus:ring-[#7EC8E3] transition-colors";

  if (confirmation) {
    return (
      <section id="prenota" ref={ref} data-testid="booking-section" className="py-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="booking-confirmation-modal"
            className="card-metallic rounded-3xl p-10 text-center"
          >
            <CheckCircle2 size={64} className="mx-auto text-[#7EC8E3]" />
            <h2 className="text-2xl sm:text-3xl font-bold mt-5">
              Grazie, {confirmation.name}!
            </h2>
            <p className="text-slate-300 mt-3">
              La tua richiesta è stata inviata con successo. Ti contatteremo a
              breve per concordare giorno e orario dell'appuntamento.
            </p>
            {confirmation.customer_email_sent && (
              <p data-testid="customer-email-notice" className="text-sm text-[#A0E0FF] mt-2">
                Ti abbiamo inviato un'email con il riepilogo della prenotazione.
              </p>
            )}
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#050B18]/70 border border-[#7EC8E3]/40 px-6 py-3">
              <span className="text-slate-400 text-sm">Preventivo stimato</span>
              <span className="text-2xl font-extrabold text-metallic font-display">
                {confirmation.total}€
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <a
                href={confirmation.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-recap-button"
                className="btn-silver rounded-full px-7 py-3 font-semibold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <MessageCircle size={18} /> Invia riepilogo su WhatsApp
              </a>
              <button
                onClick={() => setConfirmation(null)}
                data-testid="new-booking-button"
                className="btn-outline-blue rounded-full px-7 py-3 font-semibold w-full sm:w-auto"
              >
                Nuova richiesta
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="prenota" ref={ref} data-testid="booking-section" className="relative py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#7EC8E3] font-semibold">
            Richiesta di prenotazione
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3">
            Richiedi il tuo <span className="text-metallic">servizio</span>
          </h2>
          <p className="text-slate-300 mt-4">
            Componi il tuo pacchetto: il prezzo si aggiorna in tempo reale. Ti
            contattiamo noi per fissare giorno e orario.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="booking-form"
          className="grid lg:grid-cols-[1fr_320px] gap-6"
        >
          {/* Left: configuration */}
          <div className="space-y-7">
            {/* Package */}
            <div className="card-metallic rounded-2xl p-6">
              <label className="text-sm font-semibold text-slate-200">
                1. Seleziona il pacchetto
              </label>
              <div data-testid="select-package" className="grid sm:grid-cols-2 gap-3 mt-3">
                {PACKAGES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`package-option-${p.id}`}
                    onClick={() => setSelection((s) => ({ ...s, pkg: p.id }))}
                    className={`rounded-xl border px-4 py-3 text-left transition-all ${
                      pkg === p.id
                        ? "border-[#7EC8E3] bg-[#7EC8E3]/10 shadow-[0_0_18px_rgba(126,200,227,0.2)]"
                        : "border-slate-500/25 bg-[#050B18]/50 hover:border-slate-400/50"
                    }`}
                  >
                    <div className="font-bold text-slate-100">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.subtitle}</div>
                    <div className="text-sm text-[#A0E0FF] font-semibold mt-1">
                      da {p.prices.small}€
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div className="card-metallic rounded-2xl p-6">
              <label className="text-sm font-semibold text-slate-200">
                2. Dimensione dell'auto
              </label>
              <div data-testid="select-car-size" className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                {SIZES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`size-option-${s.id}`}
                    onClick={() => setSelection((sel) => ({ ...sel, size: s.id }))}
                    className={`rounded-xl border px-3 py-3 text-center transition-all ${
                      size === s.id
                        ? "border-[#7EC8E3] bg-[#7EC8E3]/10"
                        : "border-slate-500/25 bg-[#050B18]/50 hover:border-slate-400/50"
                    }`}
                    title={s.example}
                  >
                    <div className="font-semibold text-sm text-slate-100">{s.label}</div>
                    <div className="text-[10px] text-slate-500 leading-tight mt-1 line-clamp-2">
                      {s.example}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Extras */}
            <div className="card-metallic rounded-2xl p-6">
              <label className="text-sm font-semibold text-slate-200">
                3. Servizi extra (opzionali)
              </label>
              <div className="space-y-3 mt-3">
                {EXTRAS.map((ex) => (
                  <label
                    key={ex.id}
                    data-testid={`extra-service-checkbox-${ex.id}`}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all ${
                      extras.includes(ex.id)
                        ? "border-[#7EC8E3] bg-[#7EC8E3]/10"
                        : "border-slate-500/25 bg-[#050B18]/50 hover:border-slate-400/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={extras.includes(ex.id)}
                      onChange={() => toggleExtra(ex.id)}
                      className="w-4 h-4 accent-[#7EC8E3]"
                    />
                    <span className="flex-1 text-sm text-slate-200">{ex.name}</span>
                    <span className="text-sm font-semibold text-[#A0E0FF]">
                      {ex.prices[size]}€
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Location - a domicilio */}
            <div className="card-metallic rounded-2xl p-6">
              <label className="text-sm font-semibold text-slate-200">
                4. Luogo del servizio (a domicilio o in azienda)
              </label>
              <p className="text-xs text-slate-400 mt-2 mb-3">
                Veniamo noi da te. Partenza da{" "}
                <span className="text-[#A0E0FF] font-semibold">Bressanone</span>: entro 40 km la
                trasferta è gratuita, oltre +15€. Verifica la tua zona:
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input data-testid="input-location" className={inputCls}
                  placeholder="Città o indirizzo (es. Bolzano)"
                  value={location}
                  onChange={(e) => { setLocation(e.target.value); setDistanceInfo(null); }} />
                <button type="button" data-testid="check-zone-button" onClick={checkZone} disabled={checking}
                  className="btn-outline-blue rounded-xl px-5 py-3 text-sm font-semibold whitespace-nowrap disabled:opacity-60">
                  {checking ? "Verifico…" : "Verifica zona"}
                </button>
              </div>

              {distanceInfo && (
                <div data-testid="zone-result" className={`mt-3 rounded-xl border px-4 py-3 text-sm ${
                  !distanceInfo.found
                    ? "border-slate-500/30 text-slate-300"
                    : distanceInfo.within_radius
                    ? "border-green-400/40 bg-green-400/10 text-green-200"
                    : "border-amber-400/40 bg-amber-400/10 text-amber-200"
                }`}>
                  {!distanceInfo.found ? (
                    "Località non trovata. Prova con il nome del comune."
                  ) : distanceInfo.within_radius ? (
                    <>A circa <b>{distanceInfo.distance_km} km</b> da Bressanone — <b>trasferta gratuita</b></>
                  ) : (
                    <>A circa <b>{distanceInfo.distance_km} km</b> da Bressanone — supplemento trasferta <b>+{distanceInfo.surcharge}€</b></>
                  )}
                </div>
              )}
            </div>

            {/* Customer */}
            <div className="card-metallic rounded-2xl p-6">
              <label className="text-sm font-semibold text-slate-200">
                5. I tuoi dati
              </label>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <input data-testid="input-customer-name" className={inputCls} placeholder="Nome"
                  value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} required />
                <input data-testid="input-customer-lastname" className={inputCls} placeholder="Cognome"
                  value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} required />
                <input data-testid="input-customer-phone" className={inputCls} placeholder="Telefono"
                  value={form.phone} onChange={(e) => setField("phone", e.target.value)} required />
                <input data-testid="input-customer-email" type="email" className={inputCls} placeholder="Email"
                  value={form.email} onChange={(e) => setField("email", e.target.value)} required />
                <input data-testid="input-car-model" className={`${inputCls} sm:col-span-2`} placeholder="Targa auto (opzionale)"
                  value={form.plate} onChange={(e) => setField("plate", e.target.value)} />
                <textarea data-testid="input-notes" className={`${inputCls} sm:col-span-2 resize-none`} rows={3}
                  placeholder="Note aggiuntive (opzionale)"
                  value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Right: summary */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="card-metallic rounded-2xl p-6">
              <div className="flex items-center gap-2 text-slate-200">
                <CalendarCheck size={18} className="text-[#7EC8E3]" />
                <h3 className="font-bold">Riepilogo</h3>
              </div>
              <div className="metallic-divider my-4" />

              <div className="space-y-2 text-sm">
                <SummaryRow
                  label="Pacchetto"
                  value={pkg ? PACKAGES.find((p) => p.id === pkg).name : "—"}
                />
                <SummaryRow label="Dimensione" value={SIZES.find((s) => s.id === size).label} />
                {pkg && (
                  <SummaryRow
                    label="Prezzo pacchetto"
                    value={`${PACKAGES.find((p) => p.id === pkg).prices[size]}€`}
                    accent
                  />
                )}
                {extras.map((eid) => {
                  const ex = EXTRAS.find((e) => e.id === eid);
                  return (
                    <SummaryRow key={eid} label={ex.name} value={`${ex.prices[size]}€`} accent />
                  );
                })}
                {distanceInfo && distanceInfo.found && (
                  <SummaryRow
                    label={`Trasferta (${distanceInfo.distance_km} km)`}
                    value={travelSurcharge === 0 ? "Gratis" : `+${travelSurcharge}€`}
                    accent
                  />
                )}
              </div>

              <div className="metallic-divider my-4" />
              <div className="flex items-center justify-between">
                <span className="text-slate-300 font-medium">Totale</span>
                <span
                  data-testid="realtime-total-price"
                  className="text-3xl font-extrabold text-metallic font-display"
                >
                  {total}€
                </span>
              </div>

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={submitting}
                className="btn-silver w-full rounded-full py-4 mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 size={18} className="animate-spin" /> Invio…</>
                ) : (
                  "Invia richiesta di prenotazione"
                )}
              </button>
              <p className="text-[11px] text-slate-500 text-center mt-3">
                Nessun pagamento richiesto ora. Ti contattiamo per concordare
                giorno e orario.
              </p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
});

const SummaryRow = ({ label, value, accent }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-slate-400 truncate">{label}</span>
    <span className={`font-semibold shrink-0 ${accent ? "text-[#A0E0FF]" : "text-slate-100"}`}>
      {value}
    </span>
  </div>
);

Booking.displayName = "Booking";
